<script setup>
import { useField } from 'vee-validate'

const props = defineProps({
  options: {
    type: Array,
    required: true,
    default: () => [],
  },
  size: {
    type: String,
    default: 'md',
    validators: value => ['sm', 'md', 'lg'].includes(value),
  },
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: false,
  },
  direction: {
    type: String,
    default: 'row',
    validators: value => ['row', 'column'].includes(value),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})

const model = defineModel({ type: [String, null, Boolean] })

const { value, errorMessage } = useField(() => props.name, undefined, {
  syncVModel: true,
})
</script>

<template>
  <div
    class="flex flex-col gap-1 border-none px-0.5 py-0"
    :class="{
      'text-neutral-40': disabled,
    }"
  >
    <label
      class="text-neutral-60"
      :class="{
        'text-base': size === 'lg',
        'text-sm': size === 'md',
        'text-xs': size === 'sm',
      }"
      :for="$attrs.id"
    >
      {{ label }}
    </label>
    <div
      class="flex gap-4"
      :class="{
        'flex-col': direction === 'column',
        'flex-row': direction === 'row',
      }"
    >
      <div v-for="(option, index) in options" :key="index" class="flex flex-row items-center gap-2">
        <input
          type="radio"
          :id="`${$attrs.id}-${option.value}`"
          :name="$attrs.name"
          :value="option.value"
          v-model="value"
          :disabled="disabled"
          class="radio-input appearance-none"
        />
        <label
          :for="`${$attrs.id}-${option.value}`"
          class="text-neutral-60 inline-flex cursor-pointer items-center gap-1"
          :class="{
            'text-primary-90': option.value === model,
            'text-base': size === 'lg',
            'text-sm': size === 'md',
            'text-xs': size === 'sm',
          }"
        >
          {{ option.label }}
          <ImoIcon
            v-if="option.tooltip"
            v-tooltip="{ text: option.tooltip }"
            name="message-question"
            :size="14"
            class="text-neutral-40 cursor-pointer"
            :thickness="1.5"
          />
        </label>
      </div>
    </div>
    <span class="text-error-60 text-xs" :id="`password-${$attrs.id}-errors`">
      {{ errorMessage }}
    </span>
  </div>
</template>

<style scoped>
@reference "@/assets/style/main.css";

.radio-input {
  @apply w-[20px] h-[20px] disabled:bg-neutral-10 disabled:border-neutral-30 disabled:text-neutral-40 text-primary-90 cursor-pointer focus:ring-transparent focus:shadow-none focus:outline-hidden;
}

.radio-input:checked {
  @apply border-primary-60 bg-primary-90;
}

.error {
  @apply bg-error-10 border-error-60 text-error-60;
  box-shadow: 0px 0px 0px 3px #f008024d;
}
</style>
